import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { BACKEND_URL } from "../config/api";
import { Bell, X, Check } from "lucide-react";
import { toast } from "sonner";
import {
  isNotificationSupported,
  requestNotificationPermission,
  registerServiceWorker,
  subscribeToPush,
  getIOSInstallHint,
} from "../utils/notifications";

/**
 * Compact bell icon + popover modal for one-tap price/VIX/% alerts.
 *
 * Props:
 *   ticker: string            — the stock to set an alert on
 *   currentPrice?: number     — used to pre-fill threshold with a sensible default
 */
export default function QuickAlertButton({ ticker, currentPrice = null }) {
  const [open, setOpen] = useState(false);
  const [alertType, setAlertType] = useState("price_above");
  const [threshold, setThreshold] = useState("");
  const [saving, setSaving] = useState(false);
  const popoverRef = useRef(null);
  const apiBase = BACKEND_URL;

  // Pre-fill threshold based on type + current price
  useEffect(() => {
    if (!open) return;
    if (currentPrice && currentPrice > 0) {
      if (alertType === "price_above") setThreshold((currentPrice * 1.02).toFixed(2));
      else if (alertType === "price_below") setThreshold((currentPrice * 0.98).toFixed(2));
      else if (alertType === "pct_move_above") setThreshold("2");
      else if (alertType === "pct_move_below") setThreshold("-2");
      else if (alertType === "vix_above") setThreshold("25");
    }
  }, [open, alertType, currentPrice]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // Get the current browser's push endpoint (or enroll a new one)
  const getOrCreateEndpoint = async () => {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) return null;
    await registerServiceWorker();
    const reg = await navigator.serviceWorker.ready;
    let sub = await reg.pushManager.getSubscription();
    if (sub) return sub.endpoint;

    // Not subscribed — prompt permission + subscribe
    const iosHint = getIOSInstallHint();
    if (iosHint) {
      toast.warning("iPhone detected", { description: iosHint, duration: 12000 });
    }
    if (!isNotificationSupported()) {
      toast.error("Push not supported", { description: "On iPhone, install the site to Home Screen first." });
      return null;
    }
    const perm = await requestNotificationPermission();
    if (perm !== "granted") {
      toast.error("Notifications blocked", { description: "Enable notifications in your browser settings to use alerts." });
      return null;
    }
    sub = await subscribeToPush({ apiBase, tickers: [] });
    return sub ? sub.endpoint : null;
  };

  const save = async () => {
    const thr = parseFloat(threshold);
    if (!Number.isFinite(thr)) {
      toast.error("Enter a valid threshold");
      return;
    }
    setSaving(true);
    try {
      const endpoint = await getOrCreateEndpoint();
      if (!endpoint) { setSaving(false); return; }
      await axios.post(`${apiBase}/api/push/custom-alerts`, {
        endpoint,
        ticker: ticker.toUpperCase(),
        alert_type: alertType,
        threshold: thr,
      });
      const labelMap = {
        price_above: `${ticker} price > $${thr}`,
        price_below: `${ticker} price < $${thr}`,
        pct_move_above: `${ticker} > ${thr}% intraday`,
        pct_move_below: `${ticker} < ${thr}% intraday`,
        vix_above: `VIX > ${thr}`,
      };
      toast.success("Alert created", { description: labelMap[alertType] });
      setOpen(false);
      setThreshold("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create alert");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="relative inline-block" ref={popoverRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={`quick-alert-btn-${ticker}`}
        title={`Create alert for ${ticker}`}
        aria-label={`Create alert for ${ticker}`}
        className={`inline-flex items-center justify-center w-7 h-7 rounded-full border transition-colors ${
          open
            ? "bg-amber-900/60 border-amber-600 text-amber-200"
            : "bg-gray-800 border-gray-700 text-gray-400 hover:bg-amber-900/40 hover:border-amber-700 hover:text-amber-300"
        }`}
      >
        <Bell className="w-3.5 h-3.5" />
      </button>

      {open && (
        <div
          data-testid={`quick-alert-popover-${ticker}`}
          className="absolute left-0 top-9 z-50 w-80 max-w-[92vw] rounded-lg border border-gray-700 bg-slate-900 shadow-xl p-3 space-y-2"
        >
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-white">Alert me when {ticker}…</span>
            <button onClick={() => setOpen(false)} className="text-gray-500 hover:text-gray-300">
              <X className="w-4 h-4" />
            </button>
          </div>

          <select
            value={alertType}
            onChange={(e) => setAlertType(e.target.value)}
            data-testid={`quick-alert-type-${ticker}`}
            className="w-full px-2.5 py-1.5 text-sm bg-gray-950 border border-gray-700 rounded focus:border-amber-500 focus:outline-none"
          >
            <option value="price_above">Price crosses above</option>
            <option value="price_below">Price drops below</option>
            <option value="pct_move_above">% change above</option>
            <option value="pct_move_below">% change below</option>
            <option value="vix_above">VIX above</option>
          </select>

          <div className="flex items-center gap-2">
            <span className="text-gray-500 text-xs font-mono w-16">
              {alertType.startsWith("price") ? "$" : alertType === "vix_above" ? "level" : "%"}
            </span>
            <input
              type="number"
              step="any"
              value={threshold}
              onChange={(e) => setThreshold(e.target.value)}
              placeholder="Threshold"
              data-testid={`quick-alert-threshold-${ticker}`}
              className="flex-1 px-2.5 py-1.5 text-sm bg-gray-950 border border-gray-700 rounded font-mono focus:border-amber-500 focus:outline-none"
            />
          </div>

          <button
            onClick={save}
            disabled={saving}
            data-testid={`quick-alert-save-${ticker}`}
            className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 text-sm font-semibold bg-amber-600 hover:bg-amber-500 disabled:opacity-60 text-black rounded"
          >
            <Check className="w-3.5 h-3.5" />
            {saving ? "Saving…" : "Create alert"}
          </button>

          <p className="text-[10px] text-gray-500 leading-snug">
            Once created, you'll get a native push notification when the condition triggers.
            Manage all alerts in the Alerts Manager page.
          </p>
        </div>
      )}
    </div>
  );
}
